import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

type Primitive = string | number | boolean | null | undefined;
type SerializableValue = Primitive | { [key: string | number]: SerializableValue } | SerializableValue[];

export const useSessionStorageState = <TState extends SerializableValue>(
    key: string,
    initialValue?: TState,
): [TState | undefined, Dispatch<SetStateAction<TState | undefined>>, VoidFunction] => {
    const initializer = useRef((key: string) => {
        try {
            const sessionStorageValue = sessionStorage.getItem(key);

            if (sessionStorageValue !== null) {
                return JSON.parse(sessionStorageValue);
            } else {
                initialValue && sessionStorage.setItem(key, JSON.stringify(initialValue));

                return initialValue;
            }
        } catch {
            // If user is in private mode or has storage restriction
            // sessionStorage can throw. JSON.parse and JSON.stringify
            // can throw, too.
            return initialValue;
        }
    });

    // eslint-disable-next-line react-hooks/refs -- Lazy useState initialiser; the ref holds a stable function, never render output.
    const [state, setState] = useState<TState | undefined>(() => initializer.current(key));

    useLayoutEffect(() => setState(initializer.current(key)), [key]);

    const set: Dispatch<SetStateAction<TState | undefined>> = useCallback(
        (valOrFunc) => {
            try {
                const newState =
                    typeof valOrFunc === 'function'
                        ? (valOrFunc as (prevState: TState | undefined) => TState | undefined)(state)
                        : valOrFunc;

                if (typeof newState === 'undefined') return;

                const value = JSON.stringify(newState);

                sessionStorage.setItem(key, value);

                setState(JSON.parse(value));
            } catch {
                // If user is in private mode or has storage restriction
                // sessionStorage can throw. Also JSON.stringify can throw.
            }
        },
        [key, state],
    );

    const remove = useCallback(() => {
        try {
            sessionStorage.removeItem(key);
            setState(undefined);
        } catch {
            // If user is in private mode or has storage restriction
            // sessionStorage can throw.
        }
    }, [key, setState]);

    return [state, set, remove];
};
